import React from 'react';
import { useLocation } from 'react-router-dom';
import ErrorScreen from './ErrorScreen';

/**
 * Filet de sécurité de l'app : une erreur de rendu affichait jusqu'ici une page
 * blanche. Couvre aussi l'échec de chargement d'un chunk paresseux (`/gouvernance`)
 * après un déploiement — recharger la page récupère alors les nouveaux fichiers.
 *
 * `resetKey` remet l'écran à zéro quand il change (le chemin courant, voir
 * `RouteErrorBoundary`) : naviguer ailleurs depuis la barre d'adresse ou le bouton
 * retour suffit à sortir de l'erreur, sans remonter tout l'arbre à chaque navigation.
 */
export class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Garde la trace en console : sans outil de suivi d'erreurs côté front, c'est
    // la seule piste lors d'un retour d'utilisatrice.
    console.error('Erreur de rendu interceptée', error, info?.componentStack);
  }

  componentDidUpdate(prevProps, prevState) {
    // `prevState.error` est indispensable : quand c'est la page d'arrivée qui plante,
    // la clé vient de changer dans le même cycle que l'erreur. Réinitialiser à ce
    // moment-là relancerait le rendu fautif, en boucle.
    if (this.state.error && prevState.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    // Rechargements complets plutôt que navigation interne : l'état React qui a
    // planté ne doit pas être réutilisé.
    return (
      <ErrorScreen
        code="Oups"
        title="Un souci est survenu"
        message="Une erreur inattendue a interrompu l'affichage. Recharger la page suffit le plus souvent ; tes données enregistrées ne sont pas perdues."
        primaryAction={{ label: 'Recharger la page', onClick: () => window.location.reload() }}
        secondaryAction={{ label: "Retour à l'accueil", onClick: () => window.location.assign('/') }}
      />
    );
  }
}

/** Version branchée sur le routeur : se réinitialise à chaque changement de page. */
const RouteErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
};

export default RouteErrorBoundary;
